import { FC, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import styles from "./main.module.scss";
import { ConnectionStatus } from "../../hooks/useFirebaseConnection";
import { useResponsive } from "../../hooks/useResponsive";
import { ScanInProgressRoute } from "../../constants/routes";
import connectionTimedOut from '../../assets/images/connection-timed-out.svg';
import connectionSuccessful from '../../assets/images/connection-successful.gif';
import appClipQR from '../../assets/images/app-clip-qr.svg';
import { createNavigateWithParams } from "../../helpers/navigation";
import { trackAppHandoffStart, trackAppHandoffFindRetailerLink, trackMobileSessionInitiated, trackAppHandoffComplete, trackMobileSessionFail } from "../../services/google_analytics";

import { preloadAllInsoleGlbs } from "../../constants/glb-assets";
import { useScrollToTop } from "../../hooks/useScrollToTop";

preloadAllInsoleGlbs();

const AppHandoff: FC = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isIOSPhone } = useResponsive();
  const { connectionCode, connectionStatus, setupNewConnection, connectionError } = useApp();

  useEffect(() => {
    trackAppHandoffStart();
  }, []);

  useEffect(() => {
    if (connectionStatus === ConnectionStatus.CONNECTED) {
      trackAppHandoffComplete();
      trackMobileSessionInitiated();
      setTimeout(() => {
        createNavigateWithParams(navigate, searchParams)(ScanInProgressRoute, { replace: true });
      }, 2500);
    }
  }, [connectionStatus, navigate, searchParams]);

  useEffect(() => {
    if (connectionStatus === ConnectionStatus.DISCONNECTED) {
      trackMobileSessionFail();
    }
  }, [connectionStatus]);

  const rendetStartScanningDescription = () => {
    const handleRetailerLinkClick = (_e: React.MouseEvent<HTMLAnchorElement>) => {
      trackAppHandoffFindRetailerLink();
    };

    return (
      <div className={styles.cardDescription}>
        We will use your iPhone&apos;s camera to scan your feet and create customized insoles. Don&apos;t have an iPhone?&nbsp;
        <a href="https://www.superfeet.com/pages/store-locator?filter_ids=127155" target="_blank" onClick={handleRetailerLinkClick}>Scan in Store</a>
      </div>
    );
  }

  const renderStartScanningSubtitle = () => {
    const text = isIOSPhone ? "Start scanning your feet" :
      <>Scan the code above with your iPhone&apos;s camera to begin</>;

    return (
      <div className={`${styles.cardSubtitle} ${styles.textTransformNone}`}>
        {text}
      </div>
    );
  }

  const renderAppClipIOS = () => {
    if (isIOSPhone) {
      return (
        <>
          {renderStartScanningSubtitle()}
          {rendetStartScanningDescription()}
        </>
      );
    }
  };

  const renderAppClipIOSButton = () => {
    if (isIOSPhone && connectionStatus === ConnectionStatus.INITIALIZING) {
      return (
        <div className={styles.openAppClipBtnContainer}>
          <a
            href={`https://appclip.apple.com/id?p=com.superfeet.measure.clip&url=https://superfeet-poc.web.app/measure?flow=foot3DO&code=${connectionCode}`}
            className={`${styles.btnPill} ${styles.btnSecondary} ${styles.textNoWrap} ${connectionCode ? '' : styles.disabled}`}
            target="_blank"
          >
            Start Scanning
          </a>
        </div>
      )
    }
  }

  const renderAppClip = () => {
    if (!isIOSPhone) {
      return (
        <>
          <div>
            <div className={styles.appClipQRCodeContainer}>
              <img src={appClipQR} alt="App Clip QR Code" />
            </div>
          </div>
          {renderStartScanningSubtitle()}
          {rendetStartScanningDescription()}
        </>
      );
    };
  };

  const renderConnectionCode = () => {
    const renderStatus = () => {
      switch (connectionStatus) {
        case ConnectionStatus.INITIALIZING:
          return (
            <div className={styles.cardFooterText}>Awaiting Scan Data...</div>
          );
        case ConnectionStatus.CONNECTED:
          return (
            <>
              <div className={styles.cardIconImageContainer}>
                <img src={connectionSuccessful} alt="Superfeet Logo" className={styles.cardIconImage} />
              </div>
              <div className={styles.cardFooterText}>Connection Successful!</div>
            </>
          );
        case ConnectionStatus.DISCONNECTED:
          return (
            <>
              <div className={styles.cardIconImageContainer}>
                <img src={connectionTimedOut} alt="Superfeet Logo" className={styles.cardIconImage} />
              </div>
              <div className={`${styles.cardFooterText} ${styles.textDanger}`}>{connectionError || "Connection timed out"}</div>
              <div className={styles.appHandoffCardBtnContainer}>
                <button className={`${styles.btnPill} ${styles.btnLg} ${styles.btnSecondary}`} onClick={setupNewConnection}>
                  <span>{isIOSPhone ? 'Scan Again' : 'Generate New Code'}</span>
                </button>
              </div>
            </>
          );
        default:
          return "";
      }
    };

    return (
      <>
        {renderAppClip()}
        {renderAppClipIOS()}
        {!isIOSPhone && (
          <>
            <div className={styles.cardTitle}>{connectionCode ? connectionCode.split('').join(' ') : '- - - -'}</div>
            <div className={styles.cardSubtitle}>Connection Code</div>
            <div className={styles.cardDescription}>
              Enter this four digit code into the Superfeet
              <br />
              App Clip to start scanning.
            </div>
          </>
        )}
        {renderAppClipIOSButton()}
        {renderStatus()}
      </>
    );
  };

  return (
    <div className={styles.appHandoffContainer}>
      <div className={styles.blurBackgroundOverlay}></div>
      <div className={styles.appHandoffCardRow}>
        <div className={styles.appHandoffCard}>
          <div className={styles.appHandoffCardBody}>
            {renderConnectionCode()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppHandoff;
